import { zodResolver } from '@hookform/resolvers/zod'
import { getRouteApi } from '@tanstack/react-router'
import { type DefaultValues, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import Loading from '@/components/Loading'
import BasicLayout from '@/components/common/BasicLayout/BasicLayout'
import useExternalErrors from '@/hooks/useExternalErrors'
import { toast } from '@/hooks/useToast'
import { useAllEducationLevels } from '@/queries/useAllEducationLevels'
import { parseNameAndIdToLabelAndValue } from '@/utils/parse-id-and-name-to-label-and-value'

import CourseForm from '../components/CourseForm'
import {
  courseFormPayload,
  type CourseFormPayload
} from '../constants/course-payload'
import useEditCourse from '../mutations/useEditCourse'
import { useCourse, type Course } from '../queries/useCourse'
import { getFullNamePreview } from '../utils/get-full-name-preview'

const routeApi = getRouteApi('/_auth/courses-management/$courseId/edit')

const CourseEditView = () => {
  const { t } = useTranslation(['coursesManagement', 'common'])
  const { courseId } = routeApi.useParams()

  const { data: course, isLoading: isCourseLoading } = useCourse(courseId)

  return (
    <BasicLayout
      breadcrumbs={[
        {
          title: t('common:navigation.courses-management'),
          to: '/courses-management'
        },
        {
          title: t('header.edit-course'),
          to: '/courses-management/$courseId/edit',
          params: {
            courseId
          }
        }
      ]}
      header={t('header.edit-course')}
      moduleName={t('common:header.administration')}
    >
      <Loading spinning={isCourseLoading}>
        {course ? <CourseEditForm course={course} /> : null}
      </Loading>
    </BasicLayout>
  )
}

const CourseEditForm = (props: { course: Course }) => {
  const navigate = routeApi.useNavigate()
  const { t } = useTranslation('coursesManagement')
  const { data: allEducationLevels, isLoading: isAllEducationLevelsLoading } =
    useAllEducationLevels()

  const defaultValues: DefaultValues<CourseFormPayload> = {
    name: props.course.name,
    educationLevel: props.course.educationLevel?.id,
    domain: parseNameAndIdToLabelAndValue(props.course.domain),
    preferredRooms: props.course?.preferredRooms
      ? props.course.preferredRooms.map(parseNameAndIdToLabelAndValue)
      : []
  }

  const form = useForm<CourseFormPayload>({
    resolver: zodResolver(courseFormPayload()),
    mode: 'all',
    defaultValues
  })

  const {
    mutate: editCourse,
    isPending: isEditCoursePending,
    formErrors
  } = useEditCourse({
    id: props.course.id,
    onSuccess: ({ response, variables }) => {
      toast({
        variant: 'success',
        title: t('toast.course-update-success', {
          COURSE: getFullNamePreview({
            name: variables.name,
            educationLevel:
              allEducationLevels?.map?.[variables.educationLevel]?.shortName
          })
        })
      })
      navigate({
        to: '/courses-management/$courseId',
        params: {
          courseId: response.id
        }
      })
    },
    onError: ({ variables }) => {
      toast({
        variant: 'error',
        title: t('toast.course-update-error', {
          COURSE: getFullNamePreview({
            name: variables.name,
            educationLevel:
              allEducationLevels?.map?.[variables.educationLevel]?.shortName
          })
        })
      })
    }
  })
  useExternalErrors(formErrors, form)

  const isLoading = isAllEducationLevelsLoading || isEditCoursePending

  return (
    <Loading spinning={isLoading}>
      {props.course ? (
        <CourseForm
          courseId={props.course.id}
          form={form}
          onSubmit={editCourse}
        />
      ) : null}
    </Loading>
  )
}

export default CourseEditView
